import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import AuthLayout from '../components/layout/AuthLayout';
import Input, { EmailIcon, LockIcon } from '../components/ui/Input';
import Button from '../components/ui/Button';
import { authService } from '../services/authService';
import './Auth.css';

const Register = () => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    agreeToTerms: false
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  const closeError = () => {
    setErrors({});
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validatePasswordStrength = (password) => {
    if (!password) return false;
    if (password.length < 8) return false;

    const hasUpperCase = /[A-Z]/.test(password);
    const hasLowerCase = /[a-z]/.test(password);
    const hasNumber = /[0-9]/.test(password);
    const hasSymbol = /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password);

    const isValid = hasUpperCase && hasLowerCase && hasNumber && hasSymbol;
    console.log('Password validation:', {
      password: '***',
      length: password.length,
      hasUpperCase,
      hasLowerCase,
      hasNumber,
      hasSymbol,
      isValid
    });

    return isValid;
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.firstName.trim()) {
      newErrors.firstName = 'First name is required';
    }

    if (!formData.email) {
      newErrors.email = 'Email address is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 8) {
      newErrors.password = 'Password must be at least 8 characters';
    } else {
      // Check password requirements
      const hasUpperCase = /[A-Z]/.test(formData.password);
      const hasLowerCase = /[a-z]/.test(formData.password);
      const hasNumber = /[0-9]/.test(formData.password);
      const hasSymbol = /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(formData.password);

      if (!hasUpperCase) {
        newErrors.password = 'Password must contain at least one uppercase letter';
      } else if (!hasLowerCase) {
        newErrors.password = 'Password must contain at least one lowercase letter';
      } else if (!hasNumber) {
        newErrors.password = 'Password must contain at least one number';
      } else if (!hasSymbol) {
        newErrors.password = 'Password must contain at least one symbol (!@#$%^&*...)';
      }
    }

    if (!formData.agreeToTerms) {
      newErrors.agreeToTerms = 'You must agree to the Terms and Conditions';
    }

    return newErrors;
  };

  const handleSubmit = async (e) => {
    // Prevent default form submission behavior
    e.preventDefault();
    e.stopPropagation();

    const newErrors = validateForm();
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return false; // Explicitly return false to prevent any form submission
    }

    setLoading(true);
    try {
      // Prepare data according to User schema - combine firstName and lastName into name
      const registrationData = {
        name: `${formData.firstName.trim()} ${formData.lastName.trim()}`.trim(),
        email: formData.email,
        password: formData.password
      };

      const response = await authService.register(registrationData);
      console.log('Registration successful:', response);

      // Store email for potential verification resend
      localStorage.setItem('pendingEmail', formData.email);

      // Show success message briefly, then redirect
      setErrors({});
      
      // Use React Router navigate instead of window.location for better UX
      setTimeout(() => {
        window.location.href = '/login?registered=true';
      }, 1000);
    } catch (error) {
      console.error('Registration failed:', error);
      
      // Handle different types of errors
      let errorMessage = 'Registration failed. Please try again.';
      
      if (error.response) {
        const status = error.response.status;
        const data = error.response.data;
        
        if (status === 409) {
          errorMessage = 'This email is already registered. Please login instead or use a different email address.';
        } else if (status === 400) {
          errorMessage = data?.message || 'Invalid registration data. Please check your input.';
        } else if (status === 429) {
          errorMessage = 'Too many registration attempts. Please try again later.';
        } else if (status === 500) {
          errorMessage = 'Server error. Please try again later.';
        } else if (data && data.message) {
          errorMessage = data.message;
        }
      } else if (error.message) {
        // Handle specific error messages
        if (error.message.includes('User with this email already exists')) {
          errorMessage = 'This email is already registered. Please login instead or use a different email address.';
        } else if (error.message.includes('Failed to connect') || error.message.includes('API URL is not configured')) {
          errorMessage = 'Cannot connect to server. Please check your internet connection.';
        } else if (error.message.includes('Network Error')) {
          errorMessage = 'Network error. Please check your connection and try again.';
        } else {
          errorMessage = error.message;
        }
      }
      
      setErrors({
        general: errorMessage
      });
    } finally {
      setLoading(false);
    }
    
    // Explicitly return false to prevent any default form behavior
    return false;
  };


  return (
    <AuthLayout>
        <div className="auth-form">
          <div className="auth-header">
            <h1 className="auth-title">Sign up to Peleescribe .ai</h1>
          </div>

        <div className="auth-content">
          {/* Registration Form */}
          <form 
            onSubmit={handleSubmit} 
            className="auth-form-fields"
            noValidate
            autoComplete="off"
          >
            {errors.general && (
              <div className="error-message general-error">
                <span className="message-text">{errors.general}</span>
                <button 
                  type="button"
                  className="message-close-btn"
                  onClick={closeError}
                  aria-label="Close error message"
                >
                  ×
                </button>
              </div>
            )}

            {/* Name Fields Row */}
            <div className="form-row">
              <div className="form-group form-group-half">
                <div className="label-with-optional">
                  <label className="form-label">First name</label>
                  <span className="optional-text" style={{ visibility: 'hidden' }}>Optional</span>
                </div>
                <Input
                  type="text"
                  name="firstName"
                  placeholder="First name"
                  value={formData.firstName}
                  onChange={handleChange}
                  error={errors.firstName}
                />
              </div>
              <div className="form-group form-group-half">
                <div className="label-with-optional">
                  <label className="form-label">Last name</label>
                  <span className="optional-text">Optional</span>
                </div>
                <Input
                  type="text"
                  name="lastName"
                  placeholder="Last name"
                  value={formData.lastName}
                  onChange={handleChange}
                  error={errors.lastName}
                />
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">Email address</label>
              <Input
                type="email"
                name="email"
                placeholder="Email Address"
                value={formData.email}
                onChange={handleChange}
                icon={EmailIcon}
                error={errors.email}
              />
            </div>

            <div className="form-group">
              <label className="form-label">Password</label>
              <Input
                type="password"
                name="password"
                placeholder="Password"
                value={formData.password}
                onChange={handleChange}
                icon={LockIcon}
                showPasswordToggle
                error={errors.password}
                success={formData.password && validatePasswordStrength(formData.password)}
              />
              {!errors.password && formData.password && (
                <div className="password-criteria-list" style={{ marginTop: '8px', fontSize: '12px', color: '#6E7079' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px' }}>
                    <span style={{ color: formData.password.length >= 8 ? '#10b981' : '#ef4444' }}>
                      {formData.password.length >= 8 ? '✓' : '✗'}
                    </span>
                    <span style={{ color: formData.password.length >= 8 ? '#10b981' : '#6E7079' }}>
                      At least 8 characters
                    </span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px' }}>
                    <span style={{ color: /[A-Z]/.test(formData.password) ? '#10b981' : '#ef4444' }}>
                      {/[A-Z]/.test(formData.password) ? '✓' : '✗'}
                    </span>
                    <span style={{ color: /[A-Z]/.test(formData.password) ? '#10b981' : '#6E7079' }}>
                      One uppercase letter
                    </span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px' }}>
                    <span style={{ color: /[a-z]/.test(formData.password) ? '#10b981' : '#ef4444' }}>
                      {/[a-z]/.test(formData.password) ? '✓' : '✗'}
                    </span>
                    <span style={{ color: /[a-z]/.test(formData.password) ? '#10b981' : '#6E7079' }}>
                      One lowercase letter
                    </span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px' }}>
                    <span style={{ color: /[0-9]/.test(formData.password) ? '#10b981' : '#ef4444' }}>
                      {/[0-9]/.test(formData.password) ? '✓' : '✗'}
                    </span>
                    <span style={{ color: /[0-9]/.test(formData.password) ? '#10b981' : '#6E7079' }}>
                      One number
                    </span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px' }}>
                    <span style={{ color: /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(formData.password) ? '#10b981' : '#ef4444' }}>
                      {/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(formData.password) ? '✓' : '✗'}
                    </span>
                    <span style={{ color: /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(formData.password) ? '#10b981' : '#6E7079' }}>
                      One symbol (!@#$%...)
                    </span>
                  </div>
                </div>
              )}
              {!errors.password && !formData.password && (
                <div className="password-requirements">
                  Password must contain at least 8 characters, one uppercase letter, one lowercase letter, one number, and one symbol
                </div>
              )}
            </div>

            <div className="form-group">
              <div className="terms-checkbox-container">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    name="agreeToTerms"
                    checked={formData.agreeToTerms}
                    onChange={handleChange}
                    className="checkbox-input"
                  />
                  <span className="checkbox-text">
                    I agree to the <a href="https://peleescribe.com/terms_and_condition" target="_blank" rel="noopener noreferrer" className="terms-link">Terms and Conditions</a>
                  </span>
                </label>
                {errors.agreeToTerms && (
                  <div className="checkbox-error">{errors.agreeToTerms}</div>
                )}
              </div>
            </div>

            <div className="form-actions">
              <Button
                type="submit"
                variant="primary"
                size="large"
                loading={loading}
                className="submit-button"
              >
                Sign up
              </Button>
              
              <div className="auth-footer">
                <span className="auth-footer-text">Already have an account?</span>
                <Link to="/login" className="auth-footer-link">
                  Sign in
                </Link>
              </div>
            </div>
          </form>
        </div>
      </div>
    </AuthLayout>
  );
};

export default Register;
